import { useMutation, useQuery, type UseMutationOptions, type UseQueryOptions } from "@tanstack/react-query";

import type { CommandReply, RefusalError } from "./client";
import { queryKey, runCommand, runQuery, type CommandInput, type QueryParams } from "./endpoints";
import type { CommandName, CommandResponses, QueryName, QueryResponses } from "./routes";

type Options<K extends QueryName> = Omit<UseQueryOptions<QueryResponses[K]>, "queryKey" | "queryFn">;

/** Read an endpoint by name; the query key is `[name, params]`. */
export function useApiQuery<K extends QueryName>(name: K, params: QueryParams<K>, options?: Options<K>) {
  return useQuery<QueryResponses[K]>({
    ...options,
    queryKey: queryKey(name, params) as unknown as readonly unknown[],
    queryFn: () => runQuery(name, params),
  });
}

type CommandOptions<K extends CommandName> = Omit<
  UseMutationOptions<CommandReply<CommandResponses[K]>, RefusalError, CommandInput<K> & { command_id?: string }>,
  "mutationFn"
>;

/**
 * Send a command by name. A refusal arrives as a `RefusalError` with the plain sentence in `message`.
 * Pass the same `command_id` again to retry the same command (a repeat changes nothing).
 */
export function useApiCommand<K extends CommandName>(name: K, options?: CommandOptions<K>) {
  return useMutation<CommandReply<CommandResponses[K]>, RefusalError, CommandInput<K> & { command_id?: string }>({
    ...options,
    mutationFn: (input) => runCommand(name, input),
  });
}

/** The health of the back end, asked again every 30 seconds. */
export function useHealth() {
  return useApiQuery("health", {}, { refetchInterval: 30_000, retry: false, staleTime: 10_000 });
}

/**
 * The signed-in person. A refusal "not signed in" is an answer, not a failure to retry, and the
 * person is looked up again when the window gets focus again (an idle session may have ended).
 */
export function useMe() {
  return useApiQuery("me", {}, { retry: false, staleTime: 60_000, refetchOnWindowFocus: true });
}
